import { useCallback, useEffect, useRef, useState } from 'react'
import { createNadDebug } from '../utils/debugLog'
import { coordinateAppReload } from './appReload'

/**
 * Detects when a newer frontend build has been deployed while this tab is open
 * and surfaces it so the UI can nudge the user to reload.
 *
 * Mechanism (see docs/rfc/RFC-XXXX): the Vite build emits a static
 * `version.json` = `{ "version": <appBuildVersion> }` from the SAME constant
 * baked into `import.meta.env.VITE_APP_BUILD_VERSION`. This hook polls that
 * same-origin artifact and compares the served version to the running build.
 *
 * Design notes (all deliberate, hardened against adversarial review):
 * - **Fail closed.** A prompt is shown ONLY when the baked version is known,
 *   the fetch is HTTP 200, the body is small JSON with a non-empty string
 *   `version`, and it differs from the baked version. Any error / 404 /
 *   non-JSON / oversize / missing field is silently ignored — never a false
 *   prompt.
 * - **"Different", not "newer".** Git SHAs have no ordering, and that is fine:
 *   the goal is to get users onto the CURRENTLY DEPLOYED build. A rollback
 *   should also nudge open tabs on the (buggy) newer build. So a mismatch means
 *   "a new version is available".
 * - **No `?t=` cache-bust.** The prod CDN includes the query string in its
 *   cache key, so a unique query per poll would pollute the cache. `version.json`
 *   is served `no-store` (nginx + deploy workflows), and we fetch with
 *   `cache: 'no-store'`; the stable URL revalidates every time.
 * - **Anti-loop.** After the user clicks Refresh we remember the version we
 *   reloaded toward (with a timestamp). If, after reload, the same mismatch
 *   persists within a short cooldown (transient rollout / edge skew), we
 *   suppress the prompt so a mid-deploy inconsistency can't cause a reload
 *   loop. Once the cooldown expires the prompt may reappear; a *different*
 *   version is never suppressed by this guard.
 * - **Race safety.** All triggers funnel through one single-flight check
 *   (in-flight coalescing + AbortController + generation counter), so
 *   near-simultaneous focus/visibility/interval/custom-event triggers and
 *   React StrictMode's double-mount cannot leak stale state.
 */
const log = createNadDebug('dw:version-check')

const DEFAULT_INTERVAL_MS = 5 * 60 * 1000
const MIN_INTERVAL_MS = 2 * 1000
const MAX_DELAY_MS = 30 * 60 * 1000
const RELOAD_COOLDOWN_MS = 60 * 1000
const MAX_BACKOFF_EXP = 6
const MAX_VERSION_LEN = 64
const MAX_BODY_BYTES = 4096
// Bound each fetch so a request that never settles can't wedge the poll loop
// (an unsettled fetch would leave inFlightRef set and stop rescheduling).
const REQUEST_TIMEOUT_MS = 10_000

const DISMISS_PREFIX = 'dw:version:dismissed:'
const RELOADED_FOR_KEY = 'dw:version:reloaded-for'

/** Custom event that forces an immediate re-check (deterministic test hook). */
export const VERSION_RECHECK_EVENT = 'dw:version:recheck'

interface ReloadedFor {
  version: string
  attemptedAt: number
}

export interface VersionCheckState {
  /** The newly-deployed version to prompt about, or null when none. */
  newVersion: string | null
  /** Dismiss the current prompt (suppresses this version for the tab session). */
  dismiss: () => void
  /** Reload the page to pick up the new build. */
  refresh: () => void
}

/** Trim + length-bound a version string; returns null for anything invalid. */
function normalizeVersion(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed || trimmed.length > MAX_VERSION_LEN) return null
  return trimmed
}

/** Baked version this tab was built as. Constant for the tab's lifetime. */
const BAKED_VERSION = normalizeVersion(import.meta.env.VITE_APP_BUILD_VERSION)

/** Poll interval (ms); overridable via env for tests, floored at MIN_INTERVAL_MS. */
function resolveIntervalMs(): number {
  const raw = import.meta.env.VITE_VERSION_CHECK_INTERVAL_MS
  if (!raw) return DEFAULT_INTERVAL_MS
  const parsed = Number.parseInt(raw, 10)
  if (!Number.isFinite(parsed) || parsed < MIN_INTERVAL_MS) return DEFAULT_INTERVAL_MS
  return parsed
}

/** Same-origin URL of the release manifest, derived from the Vite base path. */
function versionJsonUrl(): string {
  // BASE_URL is '/' today; deriving from it keeps polling correct if a
  // non-root base is ever configured.
  const base = import.meta.env.BASE_URL || '/'
  return `${base.endsWith('/') ? base : `${base}/`}version.json`
}

/** Whether the given version was dismissed earlier this tab session. */
function readDismissed(version: string): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.sessionStorage.getItem(`${DISMISS_PREFIX}${version}`) === '1'
  } catch {
    return false
  }
}

/** Record that the given version was dismissed for this tab session. */
function persistDismissed(version: string): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(`${DISMISS_PREFIX}${version}`, '1')
  } catch {
    // sessionStorage may be blocked (incognito quota) — best-effort.
  }
}

/** Read the anti-loop reload marker, or null if absent/malformed. */
function readReloadedFor(): ReloadedFor | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(RELOADED_FOR_KEY)
    if (!raw) return null
    // JSON.parse returns `any`; assigning to a typed local narrows without a
    // cast, and the runtime typeof guards below validate the actual shape.
    const parsed: { version?: string; attemptedAt?: number } = JSON.parse(raw)
    if (typeof parsed?.version === 'string' && typeof parsed?.attemptedAt === 'number') {
      return { version: parsed.version, attemptedAt: parsed.attemptedAt }
    }
    return null
  } catch {
    return null
  }
}

/** Persist the anti-loop reload marker (version + timestamp). */
function writeReloadedFor(entry: ReloadedFor): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(RELOADED_FOR_KEY, JSON.stringify(entry))
  } catch {
    // best-effort
  }
}

/** Remove the anti-loop reload marker (called on convergence). */
function clearReloadedFor(): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(RELOADED_FOR_KEY)
  } catch {
    // best-effort
  }
}

export function useVersionCheck(): VersionCheckState {
  const [newVersion, setNewVersion] = useState<string | null>(null)

  const newVersionRef = useRef<string | null>(null)
  const inFlightRef = useRef(false)
  const genRef = useRef(0)
  const abortRef = useRef<AbortController | null>(null)
  const backoffRef = useRef(0)

  useEffect(() => {
    newVersionRef.current = newVersion
  }, [newVersion])

  const check = useCallback(async () => {
    // Fail closed if the running build's identity is unknown.
    if (!BAKED_VERSION) return
    // Pause while a prompt is already visible; coalesce concurrent triggers.
    if (newVersionRef.current) return
    if (inFlightRef.current) return

    inFlightRef.current = true
    const gen = ++genRef.current
    const controller = new AbortController()
    abortRef.current = controller
    // Abort a fetch that never settles so the poll loop keeps rescheduling.
    let timedOut = false
    const timeout = window.setTimeout(() => {
      timedOut = true
      controller.abort()
    }, REQUEST_TIMEOUT_MS)

    log({ event: 'version-check-start', trace_id: 'version-check', baked: BAKED_VERSION })

    try {
      const res = await fetch(versionJsonUrl(), {
        cache: 'no-store',
        // Same-origin static asset; `credentials: 'include'` is the console
        // fetch convention (harmless here — no cross-origin, cookies already
        // sent same-origin).
        credentials: 'include',
        signal: controller.signal,
      })
      if (gen !== genRef.current) return
      if (!res.ok) throw new Error(`status ${res.status}`)

      const text = await res.text()
      if (gen !== genRef.current) return
      if (text.length > MAX_BODY_BYTES) throw new Error('oversize')

      // JSON.parse returns `any`; assigning to a typed local narrows without a
      // cast, and normalizeVersion's runtime typeof guard validates the value.
      let parsed: { version?: string } | null
      try {
        parsed = JSON.parse(text)
      } catch {
        throw new Error('non-json')
      }
      const fetched = normalizeVersion(parsed?.version)
      if (!fetched) throw new Error('missing-version')

      backoffRef.current = 0
      log({ event: 'version-fetched', trace_id: 'version-check', fetched, baked: BAKED_VERSION })

      if (fetched === BAKED_VERSION) {
        // Converged with the served build — clear any reload marker.
        clearReloadedFor()
        log({ event: 'version-match', trace_id: 'version-check', fetched })
        return
      }

      if (readDismissed(fetched)) {
        log({ event: 'version-dismissed-suppressed', trace_id: 'version-check', fetched })
        return
      }

      const reloadedFor = readReloadedFor()
      if (
        reloadedFor &&
        reloadedFor.version === fetched &&
        Date.now() - reloadedFor.attemptedAt < RELOAD_COOLDOWN_MS
      ) {
        log({ event: 'version-anti-loop-suppressed', trace_id: 'version-check', fetched })
        return
      }

      log({ event: 'version-prompt-shown', trace_id: 'version-check', fetched, baked: BAKED_VERSION })
      setNewVersion(fetched)
    } catch (err) {
      // Aborted by unmount cleanup → bail silently. A timeout also aborts, but
      // should count as a failure (backoff), so it falls through.
      if (controller.signal.aborted && !timedOut) return
      backoffRef.current = Math.min(backoffRef.current + 1, MAX_BACKOFF_EXP)
      log({
        event: 'version-check-error',
        trace_id: 'version-check',
        error: timedOut ? 'timeout' : err instanceof Error ? err.message : String(err),
        backoff: backoffRef.current,
      })
      // Fail closed: leave newVersion untouched.
    } finally {
      window.clearTimeout(timeout)
      inFlightRef.current = false
    }
  }, [])

  const dismiss = useCallback(() => {
    const current = newVersionRef.current
    if (current) {
      persistDismissed(current)
      log({ event: 'version-dismissed', trace_id: 'version-check', dismissed: current })
    }
    setNewVersion(null)
  }, [])

  const refresh = useCallback(() => {
    const current = newVersionRef.current
    if (current) {
      writeReloadedFor({ version: current, attemptedAt: Date.now() })
    }
    log({ event: 'version-refresh-clicked', trace_id: 'version-check', target: current })
    void coordinateAppReload().then(
      () => {
        log({ event: 'version-refresh-reload-dispatched', trace_id: 'version-check', target: current })
      },
      (err) => {
        log({
          event: 'version-refresh-reload-failed',
          trace_id: 'version-check',
          target: current,
          error: err instanceof Error ? err.message : String(err),
        })
      },
    )
  }, [])

  useEffect(() => {
    if (!BAKED_VERSION) return

    const intervalMs = resolveIntervalMs()
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null

    // Function declarations (hoisted) so the loop can reference itself without a
    // use-before-define hazard.
    function scheduleNext() {
      if (cancelled) return
      const delay =
        backoffRef.current === 0
          ? intervalMs
          : Math.min(intervalMs * 2 ** backoffRef.current, MAX_DELAY_MS)
      timer = setTimeout(() => void loop(), delay)
    }

    async function loop() {
      if (cancelled) return
      if (!document.hidden) {
        await check()
      }
      scheduleNext()
    }

    const trigger = () => {
      if (cancelled) return
      void check()
    }

    const onVisibility = () => {
      if (!document.hidden) trigger()
    }

    // Initial check + periodic loop.
    void loop()

    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', trigger)
    window.addEventListener(VERSION_RECHECK_EVENT, trigger)

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
      abortRef.current?.abort()
      inFlightRef.current = false
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', trigger)
      window.removeEventListener(VERSION_RECHECK_EVENT, trigger)
    }
  }, [check])

  return { newVersion, dismiss, refresh }
}
